
import type { WaveOffset } from "./screenwave";
import {
  DEFAULT_MM_PER_PIXEL,
  MAX_MM_PER_PIXEL,
  MIN_MM_PER_PIXEL,
  clampZoom,
} from "./zoom";
import {
  DEFAULT_PITCH_DEG,
  DEFAULT_YAW_DEG,
  FOV_DEG,
  View,
  eyeDistanceMm,
  type Mat4,
} from "./view";
import type { WorldBounds } from "./window";

/** Where the camera starts. The range and the reasoning live in `zoom.ts`, which
 *  owns the setting; this is only the value a fresh `Camera` is born with. */
export const MM_PER_PIXEL_DEFAULT = DEFAULT_MM_PER_PIXEL;

/** Mouse-look pitch clamps. 27° is the original; 10–70 stays a usable view. */
export const PITCH_MIN_DEG = 10;
export const PITCH_MAX_DEG = 70;

export const CAMERA_MAX_DISTANCE_SHORT_MM = 25_000;
export const CAMERA_MAX_DISTANCE_LONG_MM = 35_000;

const FOLLOW_HALF_LIFE_MS = 90;

export const CAMERA_ROTATE_DEG_PER_SECOND = 90;

/** A bearing folded into [0, 360). Kept out of `View`, which is happy with any
 *  angle, because this is the value the HUD and a debug dump read. */
export function normaliseYawDeg(deg: number): number {
  if (!Number.isFinite(deg)) return DEFAULT_YAW_DEG;
  return ((deg % 360) + 360) % 360;
}

const CUT_DISTANCE_MM = 20_000;

/** `eCameraState` (`EterLib/Camera.h:21-29`) reduced to the two values the
 *  shipped client ever latches: `ProcessTerrainCollision` sets the can-not-go-
 *  down state and clears it (`CameraProcedure.cpp:20,40,44,52`), and the four
 *  others are only ever set inside its own commented-out blocks or not at all. */
export type CameraState = "normal" | "cant-go-down";

/** `m_fTerrainCollisionRadius`, 50 client units (`EterLib/Camera.cpp:47`), in
 *  this tree's millimetres. The eye is held twice this above the ground. */
export const TERRAIN_COLLISION_RADIUS_MM = 500;

export const VIEW_DISTANCE_STEPS_MM = {
  DISTANCE0: 256_000,
  DISTANCE1: 192_000,
  DISTANCE2: 128_000,
  DISTANCE3: 96_000,
  DISTANCE4: 64_000,
} as const;

// Match the shipped default selected by `introloading.py:390` and
// `PythonApplication.cpp:819`: 25,600 client units = 256 metres. The fog
// thresholds remain proportional, so no extra far-clip work is introduced.
export const MAX_VIEW_RADIUS_MM: number = VIEW_DISTANCE_STEPS_MM.DISTANCE0;

const FOG_START_OF_FAR_CLIP = 0.5;
const FOG_END_OF_FAR_CLIP = 0.7;

/** 128 m at the shipped step: where a recordless world starts thinning. */
export const FOG_NEAR_MM = MAX_VIEW_RADIUS_MM * FOG_START_OF_FAR_CLIP;
/** 179.2 m at the shipped step: where it is gone, with the clip still 76.8 m
 *  further out. The gap is what makes the clip unobservable, and it is
 *  proportional, so it survives any change to the step above. */
export const FOG_FAR_MM = MAX_VIEW_RADIUS_MM * FOG_END_OF_FAR_CLIP;

export interface Fog {
  /** The camera's target on the ground plane -- deliberately the target and not
   *  the eye. The eye sits tens of metres up and behind, so measuring from it
   *  would make the fade depend on the pitch and the zoom, and the ground under
   *  the player's feet would start fogging the moment the camera pulled back. */
  readonly cameraXMm: number;
  readonly cameraYMm: number;
  readonly nearMm: number;
  readonly farMm: number;
  readonly colour: readonly [number, number, number];
}

export const FOG_VERTEX_GLSL = `
uniform vec2 u_fogCameraMm;
/* (near, far), packed so the two can never be set apart from each other. */
uniform vec2 u_fogRangeMm;

out float v_fog;

float fogFactor(vec2 worldMm) {
  return smoothstep(u_fogRangeMm.x, u_fogRangeMm.y, distance(worldMm, u_fogCameraMm));
}
`;

/** The fragment half: one `mix` towards the void, using what the vertex stage
 *  already worked out. Deliberately trivial -- everything that decides *how*
 *  this world ends is above, in one place. */
export const FOG_FRAGMENT_GLSL = `
uniform vec3 u_fogColour;

in float v_fog;

vec3 applyFog(vec3 colour) {
  return mix(colour, u_fogColour, v_fog);
}
`;

/** The uniform names the two snippets declare between them, for a shader's own
 *  uniform list. */
export const FOG_UNIFORMS = ["u_fogCameraMm", "u_fogRangeMm", "u_fogColour"] as const;

export class Camera {
  /** Where the camera is looking, on the ground plane. Eased toward the player
   *  by `follow`; nothing here knows about elevation. */
  xMm = 0;
  yMm = 0;
  /** Ground height under `xMm, yMm`, in millimetres. The camera has no terrain
   *  of its own -- the renderer sets this from the heightfield each frame, which
   *  is what keeps the eye rising onto a hill with the player instead of
   *  levelling out at sea level under their feet. */
  zMm = 0;
  mmPerPixel = MM_PER_PIXEL_DEFAULT;
  pitchDeg: number = DEFAULT_PITCH_DEG;
  viewportWidth = 0;
  viewportHeight = 0;

  private maximumEyeDistanceMm = CAMERA_MAX_DISTANCE_SHORT_MM;

  /** Which way the camera faces, in degrees clockwise from north. See
   *  `ViewOptions.yawDeg`; kept folded into [0, 360) by its setter so a player
   *  who holds E for an hour does not end up with a bearing of 300,000. */
  private yaw = DEFAULT_YAW_DEG;

  private readonly view = new View();

  /** A screen wave's offset, applied to the view's target alone; see `ScreenWaves`. */
  wave: WaveOffset = { xMm: 0, yMm: 0, zMm: 0 };

  get yawDeg(): number {
    return this.yaw;
  }

  set yawDeg(deg: number) {
    this.yaw = normaliseYawDeg(deg);
  }

  rotate(deltaDeg: number): void {
    if (deltaDeg === 0) return;
    this.yawDeg = this.yaw + deltaDeg;
  }

  /** Mouse-look pitch. Original default is 27° (`DEFAULT_PITCH_DEG`); the
   *  clamps keep the eye off the zenith and off the ground plane. Drag down
   *  increases pitch (look more downward). */
  pitchBy(deltaDeg: number): void {
    // While the eye is sitting on the ground, the half of the drag that would
    // push it further down is dropped rather than fought: `Drag` clamps the
    // pitch velocity to zero from below in exactly this state
    // (`EterLib/Camera.cpp:161-163`).
    if (this.state === "cant-go-down") deltaDeg = Math.max(0, deltaDeg);
    if (deltaDeg === 0) return;
    this.pitchDeg = Math.min(PITCH_MAX_DEG, Math.max(PITCH_MIN_DEG, this.pitchDeg + deltaDeg));
  }

  private state: CameraState = "normal";

  /** `GetCameraState` (`EterLib/Camera.h:154`), which is what `Drag` reads. */
  get cameraState(): CameraState {
    return this.state;
  }

  /** `SetCameraState` (`EterLib/Camera.cpp:178-198`): a change of state, and
   *  nothing when the state is already the one being set. */
  setCameraState(next: CameraState): void {
    if (next === this.state) return;
    this.state = next;
  }

  /** Keep the eye twice the collision radius above the ground under it, and
   *  latch the state that refuses the drag that put it there.
   *
   * `ProcessTerrainCollision` (`UserInterface/CameraProcedure.cpp:13-53`) casts
   * the target-to-camera-bottom and camera-bottom-to-terrain rays against the
   * terrain alone (`:18,39`, `EterLib/Camera.cpp:293-306`), and on a hit lifts
   * the eye to `GetHeight` under it -- terrain and object surfaces both
   * (`:21`, `GameLib/MapOutdoor.cpp:746-773`) -- plus twice the radius, never
   * lowering it. Here the eye is not a stored point but the end of a pitch, so
   * the lift is the pitch that reaches the same height; the two rays collapse
   * into the one question they both answer, whether the terrain is inside that
   * radius of the eye.
   */
  avoidGround(ground: {
    terrainAt(xMm: number, yMm: number): number;
    at(xMm: number, yMm: number): number;
  }): void {
    const eye = this.eyeMm;
    const clearanceMm = 2 * TERRAIN_COLLISION_RADIUS_MM;
    if (eye.zMm >= ground.terrainAt(eye.xMm, eye.yMm) + clearanceMm) {
      this.setCameraState("normal");
      return;
    }
    this.setCameraState("cant-go-down");
    const floorMm = ground.at(eye.xMm, eye.yMm) + clearanceMm;
    const reachMm = eyeDistanceMm(this.mmPerPixel, this.viewportHeight);
    const rise = (floorMm - this.zMm) / reachMm;
    // Always upward, without needing to say so: the eye is under the floor, so
    // the pitch that reaches the floor is above the pitch it has. This is the
    // `SetEye` the original only ever makes for a higher eye
    // (`CameraProcedure.cpp:22-31,44-50`), with the clamp doing the refusing.
    const wantedDeg = rise >= 1 ? PITCH_MAX_DEG : (Math.asin(rise) * 180) / Math.PI;
    this.pitchDeg = Math.min(PITCH_MAX_DEG, wantedDeg);
  }

  /** Jump straight to a position, for spawn and for teleports. */
  snapTo(xMm: number, yMm: number): void {
    this.xMm = xMm;
    this.yMm = yMm;
  }

  followedXMm = 0;
  followedYMm = 0;

  follow(targetXMm: number, targetYMm: number, dtMs: number): void {
    this.followedXMm = targetXMm;
    this.followedYMm = targetYMm;
    // A target further than a camera could reasonably chase is a relocation, not
    // a movement: a respawn, and later every warp. Eased, it is a two-second
    // flight across the map with the player's character somewhere off-screen
    // ahead of it; cut, it is where they now are.
    const jump = Math.hypot(targetXMm - this.xMm, targetYMm - this.yMm);
    if (jump > CUT_DISTANCE_MM) {
      this.snapTo(targetXMm, targetYMm);
      return;
    }

    // Exponential decay toward the target: the fraction covered depends on
    // elapsed time, not on how many frames happened to fit into it.
    const t = 1 - Math.pow(0.5, dtMs / FOLLOW_HALF_LIFE_MS);
    this.xMm += (targetXMm - this.xMm) * t;
    this.yMm += (targetYMm - this.yMm) * t;
  }

  resize(width: number, height: number): void {
    this.viewportWidth = width;
    this.viewportHeight = height;
  }

  /** Largest permitted world scale for the current viewport and camera cap. */
  get maxMmPerPixel(): number {
    // `eyeDistanceMm` is linear in mmPerPixel (`view.ts:246-253`), so invert
    // that exact projection rather than approximating from a nominal monitor.
    const height = Math.max(1, this.viewportHeight);
    const projected =
      (this.maximumEyeDistanceMm * 2 * Math.tan((FOV_DEG * Math.PI) / 360)) / height;
    // The normal zoom floor remains load-bearing: below it, the server's
    // interest radius can fall outside the screen.  A tall browser therefore
    // reaches the safe minimum rather than creating a new blind spot.
    return Math.max(MIN_MM_PER_PIXEL, Math.min(MAX_MM_PER_PIXEL, projected));
  }

  setMaximumEyeDistanceMm(distanceMm: number): void {
    if (distanceMm !== CAMERA_MAX_DISTANCE_SHORT_MM && distanceMm !== CAMERA_MAX_DISTANCE_LONG_MM) {
      throw new RangeError("camera distance must be one of the source-backed limits");
    }
    this.maximumEyeDistanceMm = distanceMm;
    this.mmPerPixel = this.clampZoom(this.mmPerPixel);
  }

  /** Clamp interactive zoom to both the global safe range and this local cap. */
  clampZoom(mmPerPixel: number): number {
    return Math.min(clampZoom(mmPerPixel), this.maxMmPerPixel);
  }

  update(): void {
    this.view.update({
      targetXMm: this.xMm + this.wave.xMm,
      targetYMm: this.yMm + this.wave.yMm,
      targetZMm: this.zMm + this.wave.zMm,
      viewportWidth: this.viewportWidth,
      viewportHeight: this.viewportHeight,
      mmPerPixel: this.mmPerPixel,
      pitchDeg: this.pitchDeg,
      yawDeg: this.yaw,
    });
  }

  /** The GPU's half of the current projection, for the terrain shader. */
  get viewProj(): Mat4 {
    return this.view.viewProj;
  }

  /** See `View.view`: the eye's own frame, for the blocker pass. */
  get viewMatrix(): Mat4 {
    return this.view.view;
  }

  /** Eye to target, in millimetres -- the original's `CCamera::GetDistance`
   *  (`EterLib/Camera.h:184`), which is the radius `__CollectCollisionPCBlocker`
   *  sweeps with (`MapOutdoorUpdate.cpp:236,262`). */
  get eyeDistanceMm(): number {
    const eye = this.eyeMm;
    return Math.hypot(eye.xMm - this.xMm, eye.yMm - this.yMm, eye.zMm - this.zMm);
  }

  /** The three points the blocker search works between: where the eye is,
   *  what it looks at, and where the player stands. `__Game_UpdateArea` sweeps
   *  to the player and applies its near-side test against the camera's target
   *  (`MapOutdoorUpdate.cpp:236-262`), so they are kept apart here too. */
  get pcBlockerFrame(): {
    readonly eyeMm: Vec3Mm;
    readonly targetMm: Vec3Mm;
    readonly playerMm: Vec3Mm;
    readonly distanceMm: number;
  } {
    const eye = this.eyeMm;
    return {
      eyeMm: [eye.xMm, eye.yMm, eye.zMm],
      targetMm: [this.xMm, this.yMm, this.zMm],
      playerMm: [this.followedXMm, this.followedYMm, this.zMm],
      distanceMm: this.eyeDistanceMm,
    };
  }

  /** Where the eye actually sits, in world millimetres -- distinct from
   *  `xMm, yMm, zMm`, which is where the camera is *looking*. Sorting props
   *  front-to-back needs the eye, not the target: a tree behind the target but
   *  still nearer the eye than a building beyond it must draw first. */
  get eyeMm(): { readonly xMm: number; readonly yMm: number; readonly zMm: number } {
    return { xMm: this.view.eyeXMm, yMm: this.view.eyeYMm, zMm: this.view.eyeZMm };
  }

  fog(
    colour: readonly [number, number, number],
    nearMm: number = FOG_NEAR_MM,
    farMm: number = FOG_FAR_MM,
  ): Fog {
    return {
      cameraXMm: this.xMm,
      cameraYMm: this.yMm,
      nearMm,
      farMm,
      colour,
    };
  }

  /** See `View.billboardRight`. */
  get billboardRight(): readonly [number, number, number] {
    return this.view.billboardRight;
  }

  /** A world point in canvas pixels. See `View.project`. */
  project(xMm: number, yMm: number, zMm = 0): ReturnType<View["project"]> {
    return this.view.project(xMm, yMm, zMm);
  }

  /** Where a screen pixel meets a ground plane. See `View.unproject`. */
  unproject(px: number, py: number, groundZMm = 0): ReturnType<View["unproject"]> {
    return this.view.unproject(px, py, groundZMm);
  }

  visibleBounds(
    marginMm = 0,
    groundRangeMm?: { readonly minMm: number; readonly maxMm: number },
  ): WorldBounds {
    const zCandidates =
      groundRangeMm === undefined
        ? [this.zMm]
        : [this.zMm, groundRangeMm.minMm, groundRangeMm.maxMm];
    const corners: ReadonlyArray<readonly [number, number]> = [
      [0, 0],
      [this.viewportWidth, 0],
      [0, this.viewportHeight],
      [this.viewportWidth, this.viewportHeight],
    ];

    let minXMm = Number.POSITIVE_INFINITY;
    let maxXMm = Number.NEGATIVE_INFINITY;
    let minYMm = Number.POSITIVE_INFINITY;
    let maxYMm = Number.NEGATIVE_INFINITY;

    for (const groundZMm of zCandidates) {
      for (const [px, py] of corners) {
        let point = this.view.unproject(px, py, groundZMm);
        for (let step = 1; point === null && step <= 8; step++) {
          // Sky at this corner: step down its own column toward the bottom of
          // the screen, which is always ground (the camera looks down, never up).
          point = this.view.unproject(px, py + (step * (this.viewportHeight - py)) / 8, groundZMm);
        }
        if (point === null) continue;
        minXMm = Math.min(minXMm, point.xMm);
        maxXMm = Math.max(maxXMm, point.xMm);
        minYMm = Math.min(minYMm, point.yMm);
        maxYMm = Math.max(maxYMm, point.yMm);
      }
    }

    if (!Number.isFinite(minXMm)) {
      // Every corner was sky -- a viewport of zero height, or a pitch shallower
      // than this renderer ever sets. A box around the target beats an empty
      // window, which would draw nothing at all.
      const half = Math.max(1, this.viewportWidth, this.viewportHeight) * this.mmPerPixel;
      return this.withinViewRadius({
        minXMm: this.xMm - half - marginMm,
        maxXMm: this.xMm + half + marginMm,
        minYMm: this.yMm - half - marginMm,
        maxYMm: this.yMm + half + marginMm,
      });
    }

    return this.withinViewRadius({
      minXMm: minXMm - marginMm,
      maxXMm: maxXMm + marginMm,
      minYMm: minYMm - marginMm,
      maxYMm: maxYMm + marginMm,
    });
  }

  private withinViewRadius(bounds: WorldBounds): WorldBounds {
    return {
      minXMm: Math.max(bounds.minXMm, this.xMm - MAX_VIEW_RADIUS_MM),
      maxXMm: Math.min(bounds.maxXMm, this.xMm + MAX_VIEW_RADIUS_MM),
      minYMm: Math.max(bounds.minYMm, this.yMm - MAX_VIEW_RADIUS_MM),
      maxYMm: Math.min(bounds.maxYMm, this.yMm + MAX_VIEW_RADIUS_MM),
    };
  }
}

/** A sphere dragged from one point to another: the original's
 *  `CDynamicSphereInstance`, whose `v3LastPosition` and `v3Position` are the
 *  two ends of one sweep (`EterLib/CollisionData.h:51-56`). */
export interface SweptSphere {
  readonly fromMm: Vec3Mm;
  readonly toMm: Vec3Mm;
  readonly radiusMm: number;
}

export type Vec3Mm = readonly [number, number, number];

/** A prop offered to `collectPcBlockers`: its world bounding sphere, and
 *  whether it is foliage. The original asks the instance's own type --
 *  `TREE_OBJECT` goes straight in, `THING_OBJECT` and a building actor take
 *  the near-side test (`MapOutdoorUpdate.cpp:557-576`). */
export interface PcBlockerCandidate {
  readonly centreMm: Vec3Mm;
  readonly radiusMm: number;
  readonly isTree: boolean;
  /** A tree's static collision instances in world millimetres; a tree is
   *  taken on these alone, never on its bounding sphere. */
  readonly shapes?: TreeShapes;
}

/** One placed tree's `.spt` collision objects: cylinders `[x, y, z, radius,
 *  height]` and spheres `[x, y, z, radius]`, z in world millimetres. */
export interface TreeShapes {
  readonly cylinders: readonly (readonly number[])[];
  readonly spheres: readonly (readonly number[])[];
}

/** `treecollision/<map>.json` as `tools/build_tree_collision.py` writes it. */
export interface TreeCollisionData {
  readonly cylinders?: readonly (readonly number[])[];
  readonly spheres?: readonly (readonly number[])[];
  readonly treeShapes?: readonly (readonly number[])[];
}

/** Each placed tree's own objects, cut out of a map's pooled lists by the
 *  per-tree counts and stood on the tree's base height, as the wrapper
 *  translates its objects by the tree's position (`SpeedTreeWrapper.cpp:234-262`). */
export function treeShapesFor(
  data: TreeCollisionData,
  positions: readonly { readonly xMm: number; readonly yMm: number; readonly zMm: number }[],
): TreeShapes[] {
  const counts = data.treeShapes ?? [];
  const cylinders = data.cylinders ?? [];
  const spheres = data.spheres ?? [];
  const out: TreeShapes[] = [];
  let nextCylinder = 0;
  let nextSphere = 0;
  for (let index = 0; index < positions.length; index += 1) {
    const [cylinderCount, sphereCount] = counts[index] ?? [0, 0];
    const baseMm = positions[index].zMm;
    out.push({
      cylinders: cylinders.slice(nextCylinder, nextCylinder + cylinderCount)
        .map(([x, y, z, radius, height]) => [x, y, z + baseMm, radius, height]),
      spheres: spheres.slice(nextSphere, nextSphere + sphereCount)
        .map(([x, y, z, radius]) => [x, y, z + baseMm, radius]),
    });
    nextCylinder += cylinderCount;
    nextSphere += sphereCount;
  }
  return out;
}

/** `MY_EPSILON`, 0.1 of a 2014 unit (`EterLib/lineintersect_utils.cpp:25`), as
 *  a length in millimetres. */
const SEGMENT_EPSILON_MM = 1;

function dot(a: Vec3Mm, b: Vec3Mm): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function along(origin: Vec3Mm, direction: Vec3Mm, t: number): Vec3Mm {
  return [origin[0] + t * direction[0], origin[1] + t * direction[1], origin[2] + t * direction[2]];
}

function clamp01(t: number): number {
  return Math.max(0, Math.min(1, t));
}

/** `FindNearestPointOnLineSegment` (`lineintersect_utils.cpp:624-654`): the
 *  parameter along `a1 + t*l` nearest `b`, clamped to the segment unless
 *  `infinite`. */
function nearestParameter(a1: Vec3Mm, l: Vec3Mm, b: Vec3Mm, infinite: boolean): number {
  const d = dot(l, l);
  if (d < SEGMENT_EPSILON_MM * SEGMENT_EPSILON_MM) return 0;
  const t = dot(l, subtract(b, a1)) / d;
  return infinite ? t : clamp01(t);
}

/** `IntersectLineSegments` (`lineintersect_utils.cpp:222-312`): the nearest
 *  point on segment A to segment B and on B to A. */
export function nearestSegmentPoints(a1: Vec3Mm, a2: Vec3Mm, b1: Vec3Mm, b2: Vec3Mm): [Vec3Mm, Vec3Mm] {
  const la = subtract(a2, a1);
  const lb = subtract(b2, b1);
  const l11 = dot(la, la);
  const l22 = dot(lb, lb);
  const epsilonSq = SEGMENT_EPSILON_MM * SEGMENT_EPSILON_MM;
  if (l11 < epsilonSq) return [a1, along(b1, lb, nearestParameter(b1, lb, a1, false))]; // :242-247
  if (l22 < epsilonSq) return [along(a1, la, nearestParameter(a1, la, b1, false)), b1]; // :249-254
  const ab = subtract(b1, a1);
  const l12 = -dot(la, lb);
  const det = l11 * l22 - l12 * l12;
  // `FABS(DetL) < epsilon` (`:266`), a length to the fourth power: 0.1 unit^4.
  if (Math.abs(det) < 0.1 * 10 ** 4) {
    // `FindNearestPointOfParallelLineSegments` (`:691-762`).
    const s0 = nearestParameter(a1, la, b1, true);
    const s1 = nearestParameter(a1, la, b2, true);
    if (s0 < 0 && s1 < 0) return [a1, s0 < s1 ? b2 : b1];
    if (s0 > 1 && s1 > 1) return [a2, s0 < s1 ? b1 : b2];
    const onA = along(a1, la, 0.5 * (clamp01(s0) + clamp01(s1)));
    return [onA, along(b1, lb, nearestParameter(b1, lb, onA, true))];
  }
  const ra = dot(la, ab);
  const rb = -dot(lb, ab);
  let t = (l11 * rb - ra * l12) / det; // :281
  let s = (ra - l12 * t) / l11; // :286
  const outOfRange = (v: number): boolean => v < 0 || v > 1; // :23
  if (!outOfRange(s) && !outOfRange(t)) return [along(a1, la, s), along(b1, lb, t)];
  // `AdjustNearestPoints` (`:791-849`).
  if (outOfRange(s) && outOfRange(t)) {
    s = clamp01(s);
    let onA = along(a1, la, s);
    t = nearestParameter(b1, lb, onA, true);
    if (!outOfRange(t)) return [onA, along(b1, lb, t)];
    const onB = along(b1, lb, clamp01(t));
    onA = along(a1, la, nearestParameter(a1, la, onB, false));
    return [onA, along(b1, lb, nearestParameter(b1, lb, onA, false))];
  }
  if (outOfRange(s)) {
    const onA = along(a1, la, clamp01(s));
    return [onA, along(b1, lb, nearestParameter(b1, lb, onA, false))];
  }
  const onB = along(b1, lb, clamp01(t));
  return [along(a1, la, nearestParameter(a1, la, onB, false)), onB];
}

/** `CCylinderCollisionInstance::CollideCylinderVSDynamicSphere`
 *  (`EterLib/CollisionData.cpp:451-468`): the swept sphere's end within the
 *  cylinder's height band, then the nearest points of the axis and the sweep
 *  within the summed radii. */
export function sweptSphereTouchesCylinder(sweep: SweptSphere, cylinder: readonly number[]): boolean {
  const [x, y, z, radius, height] = cylinder;
  if (sweep.toMm[2] + sweep.radiusMm < z) return false; // CollisionData.cpp:453-454
  if (sweep.toMm[2] - sweep.radiusMm > z + height) return false; // CollisionData.cpp:456-457
  const [onAxis, onSweep] = nearestSegmentPoints([x, y, z], [x, y, z + height], sweep.fromMm, sweep.toMm);
  const reach = radius + sweep.radiusMm;
  return lengthSq(subtract(onAxis, onSweep)) <= reach * reach; // CollisionData.cpp:467
}

/** `CGraphicObjectInstance::CollisionDynamicSphere` over a tree's static
 *  collision instances (`GrpObjectInstance.cpp:332-341`). */
function sweptSphereTouchesTree(sweep: SweptSphere, shapes: TreeShapes): boolean {
  return shapes.cylinders.some((cylinder) => sweptSphereTouchesCylinder(sweep, cylinder))
    || shapes.spheres.some(([x, y, z, radius]) => sweptSphereTouches(sweep, [x, y, z], radius));
}

/** How many blockers one frame may collect: `PCBlocker_SInstanceList`'s own
 *  `CAPACITY` (`MapOutdoorUpdate.cpp:447`). Everything past it is counted and
 *  dropped there, and dropped here. */
export const PC_BLOCKER_CAPACITY = 512;

function subtract(a: Vec3Mm, b: Vec3Mm): Vec3Mm {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function lengthSq(v: Vec3Mm): number {
  return v[0] * v[0] + v[1] * v[1] + v[2] * v[2];
}

function midpoint(a: Vec3Mm, b: Vec3Mm): Vec3Mm {
  // `v3Eye + 0.5f * (v3Target - v3Eye)` (`MapOutdoorUpdate.cpp:597`).
  return [a[0] + 0.5 * (b[0] - a[0]), a[1] + 0.5 * (b[1] - a[1]), a[2] + 0.5 * (b[2] - a[2])];
}

/** `square_distance_between_linesegment_and_point` (`EterLib/GrpMath.h:80-97`),
 *  in millimetres squared. */
export function segmentPointDistanceSq(p1: Vec3Mm, p2: Vec3Mm, x: Vec3Mm): number {
  const seg = subtract(p2, p1);
  const toX = subtract(x, p1);
  const l = lengthSq(seg);
  const d = toX[0] * seg[0] + toX[1] * seg[1] + toX[2] * seg[2];
  if (d <= 0) return lengthSq(toX);
  if (d >= l) return lengthSq(subtract(x, p2));
  const cross: Vec3Mm = [
    toX[1] * seg[2] - toX[2] * seg[1],
    toX[2] * seg[0] - toX[0] * seg[2],
    toX[0] * seg[1] - toX[1] * seg[0],
  ];
  return lengthSq(cross) / l;
}

/** The four swept spheres the blocker search runs along the eye-to-player
 *  segment (`MapOutdoorUpdate.cpp:592-618`): eye to midpoint, midpoint to the
 *  player, and both again the other way, each of radius half the camera's own
 *  distance. The two reversed sweeps cover the same ground as the first two --
 *  `segmentPointDistanceSq` does not care which end it starts from -- but they
 *  are what the original lays down, and a reader comparing the two files
 *  should find the same four. */
export function pcBlockerSweep(
  eyeMm: Vec3Mm,
  playerMm: Vec3Mm,
  cameraDistanceMm: number,
): readonly SweptSphere[] {
  const radiusMm = cameraDistanceMm * 0.5;
  const midMm = midpoint(eyeMm, playerMm);
  return [
    { fromMm: eyeMm, toMm: midMm, radiusMm },
    { fromMm: midMm, toMm: playerMm, radiusMm },
    { fromMm: playerMm, toMm: midMm, radiusMm },
    { fromMm: midMm, toMm: eyeMm, radiusMm },
  ];
}

/** Does a swept sphere touch a static one?
 *  `CSphereCollisionInstance::OnCollisionDynamicSphere`
 *  (`EterLib/CollisionData.cpp:242-250`): strictly inside the summed radii. */
export function sweptSphereTouches(
  sweep: SweptSphere,
  centreMm: Vec3Mm,
  radiusMm: number,
): boolean {
  const reach = radiusMm + sweep.radiusMm;
  return segmentPointDistanceSq(sweep.fromMm, sweep.toMm, centreMm) < reach * reach;
}

/** Which candidates stand between the eye and the player, back to front.
 *
 * `__CollectCollisionPCBlocker` (`MapOutdoorUpdate.cpp:584-676`) sweeps the
 * four spheres down the eye-to-player segment, takes foliage on the collision
 * alone and everything else only when `__AppendObject`'s near-side test
 * (`:529-543`) puts its centre on the eye's side of the camera target, then
 * orders what it kept by descending distance from the eye
 * (`FPCBlockerDistanceSort`, `:191-200`) so the furthest is drawn first.
 */
export function collectPcBlockers(
  candidates: readonly PcBlockerCandidate[],
  eyeMm: Vec3Mm,
  targetMm: Vec3Mm,
  playerMm: Vec3Mm,
  cameraDistanceMm: number,
): number[] {
  const sweep = pcBlockerSweep(eyeMm, playerMm, cameraDistanceMm);
  // `m_v3View`, the unit vector from the eye to the target, flattened to the
  // ground plane exactly as `PCBlocker_SInstanceList`'s constructor does
  // (`MapOutdoorUpdate.cpp:470-477`): it takes x and y and drops z.
  const viewX = targetMm[0] - eyeMm[0];
  const viewY = targetMm[1] - eyeMm[1];

  const kept: number[] = [];
  for (let index = 0; index < candidates.length; index += 1) {
    if (kept.length >= PC_BLOCKER_CAPACITY) break;
    const candidate = candidates[index];
    const shapes = candidate.shapes;
    const touched = candidate.isTree && shapes !== undefined
      ? sweep.some((one) => sweptSphereTouchesTree(one, shapes))
      : sweep.some((one) => sweptSphereTouches(one, candidate.centreMm, candidate.radiusMm));
    if (!touched) continue;
    if (!candidate.isTree) {
      const toCentreX = candidate.centreMm[0] - targetMm[0];
      const toCentreY = candidate.centreMm[1] - targetMm[1];
      if (viewX * toCentreX + viewY * toCentreY > 0) continue;
    }
    kept.push(index);
  }

  kept.sort(
    (a, b) =>
      lengthSq(subtract(candidates[b].centreMm, eyeMm)) -
      lengthSq(subtract(candidates[a].centreMm, eyeMm)),
  );
  return kept;
}
